import type { ImgHTMLAttributes } from 'react'
import React, {
    forwardRef,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react'
import { useInView } from 'react-intersection-observer'

import { BREAKPOINTS } from '../../lib/hooks/useMedia'
import type { TestId } from '../../lib/utils/testid'

export const ResponsiveImage = forwardRef<
    HTMLImageElement,
    ResponsiveImageProps
>(
    (
        {
            src,
            alt,
            title,
            lazy = true,
            rootMargin = '500px',
            breakpoint = 'medium',
            onLoad,
            className,
            style,
        },
        ref
    ) => {
        const [inViewRef, inView] = useInView({ rootMargin, triggerOnce: true })
        const canLoad = inView || !lazy
        const [loaded, setLoaded] = useState(false)
        const imageRef = useRef<HTMLImageElement | null>(null)

        const [defaultSrc, alternateSrc] = Array.isArray(src)
            ? src
            : [src, null]
        const altString = alt ?? title ?? ''

        if (!defaultSrc) {
            throw new Error(
                `Invalid source provided to ResponsiveImage: ${src?.toString()}`
            )
        }

        const handleLoad = useCallback(
            (evt: any) => {
                if (!loaded && imageRef.current) {
                    if (onLoad) onLoad(evt)
                    setLoaded(true)
                    imageRef.current.removeEventListener('load', handleLoad)
                }
            },
            [loaded, onLoad]
        )

        const setRefs = useCallback((element: HTMLImageElement) => {
            inViewRef(element)
            imageRef.current = element
            if (ref) {
                if ('current' in ref) ref.current = element
                else ref(element)
            }
        }, [])

        useEffect(() => {
            if (canLoad && !loaded && imageRef.current) {
                const imageLoaded = !!(
                    (imageRef.current.complete ||
                        imageRef.current.naturalWidth) &&
                    imageRef.current.src
                )
                if (imageLoaded) handleLoad(null)
                else imageRef.current.addEventListener('load', handleLoad)
            }
        }, [canLoad, handleLoad, loaded, lazy])

        const imageElement = (
            <img
                ref={setRefs}
                src={canLoad ? defaultSrc : undefined}
                alt={altString}
                title={title}
                className={className}
                style={style}
            />
        )

        return alternateSrc ? (
            <picture>
                <source
                    data-testid={'responsive-image-source' as TestId}
                    media={`(min-width: ${BREAKPOINTS[breakpoint]})`}
                    srcSet={alternateSrc}
                />
                {imageElement}
            </picture>
        ) : (
            imageElement
        )
    }
)

ResponsiveImage.displayName = 'ResponsiveImage'

export type ResponsiveSource = string | [string] | [string, string]

type DefaultImageProps = ImgHTMLAttributes<HTMLImageElement>
export interface ResponsiveImageProps extends Omit<DefaultImageProps, 'src'> {
    lazy?: boolean
    rootMargin?: string
    src: ResponsiveSource
    breakpoint?: keyof typeof BREAKPOINTS
}
